/**********************************************************************************************************
 *   BASE IMPORT
 **********************************************************************************************************/
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import React, { useRef } from 'react'
import { ReactElement } from 'react-markdown/lib/react-markdown'

/**********************************************************************************************************
 *   SHARED
 **********************************************************************************************************/
import { RenderPropOrInvokeChildren } from 'components/renderPropOrInvokeChildren'

/**********************************************************************************************************
 *   CONSTS
 **********************************************************************************************************/
import { _BubbleAction, _BubbleActionLink } from 'components/bubble/action'
import { BubbleContext } from 'components/bubble/context'
import { BubbleStyles as S } from './style'

/**********************************************************************************************************
 *   TYPE DEFINITIONS
 **********************************************************************************************************/
type TBubbleBaseProps = {
    /**
     * Icon to display in the card. If this is not provided, a default icon is used as a placeholder
     */
    icon?: React.FC<React.RefAttributes<SVGSVGElement>>
    className?: string
    type?: 'data' | 'compound'

    /**
     * If true, clicking anywhere on the bubble will trigger the action passed as a prop or compound action
     */
    anywhereAction?: boolean
}

type TBubbleChildrenProps = TBubbleBaseProps & {
    /**
     * Optional children that can be used to render a more customized bubble
     */
    children: React.ReactNode
}

type TBubbleDataProps = TBubbleBaseProps & {
    /**
     * Title of the bubble
     */
    title: string

    /**
     * Description of the bubble
     */
    description: React.ReactNode
    action?: {
        label: string
        onClick: () => void
    }
}

type TBubblePropsUnion = TBubbleChildrenProps | TBubbleDataProps
type TBubbleProps<T extends 'data' | 'compound' = 'data'> = T extends 'compound' ? TBubbleChildrenProps : TBubbleDataProps
type TBubble = <Type extends 'data' | 'compound' = 'data'>(props: TBubbleProps<Type> & { type?: Type }) => ReactElement

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export const BaseBubble: TBubble = ({ icon: Icon, className, anywhereAction, ...props }) => {
    /***** FUNCTIONS *****/
    const actionOnclickRef = useRef<() => void>()
    const handleClick = () => {
        if (anywhereAction) {
            actionOnclickRef.current?.()
        }
    }

    /***** RENDER *****/
    return (
        <S.Bubble as={anywhereAction ? 'button' : 'div'} className={className} onClick={handleClick} data-anywhere={anywhereAction}>
            <BubbleContext.Provider value={{ actionOnclickRef }}>
                <RenderPropOrInvokeChildren props={props as unknown as TBubblePropsUnion} prop='children'>
                    {({ description, title, action }) => (
                        <>
                            <S.Title>{title}</S.Title>
                            <S.Description>{description}</S.Description>
                            {action && <_BubbleAction onClick={action.onClick}>{action.label}</_BubbleAction>}
                        </>
                    )}
                </RenderPropOrInvokeChildren>
                <S.IconWrapper>{Icon ? <Icon /> : <ArrowRightIcon />}</S.IconWrapper>
            </BubbleContext.Provider>
        </S.Bubble>
    )
}
/**********************************************************************************************************
 *   COMPONENT END
 **********************************************************************************************************/

/***** BUBBLE VARIANTS *****/
const CompoundBubble = (props: Omit<TBubbleProps<'compound'>, 'type'>) => BaseBubble({ ...props, type: 'compound' })
const DataBubble = (props: Omit<TBubbleProps<'data'>, 'type'>) => BaseBubble({ ...props, type: 'data' })

/***** EXPORTS *****/
export const Bubble = Object.assign(DataBubble, {
    Title: S.Title,
    Description: S.Description,
    Compound: CompoundBubble,
    Action: Object.assign(_BubbleAction, {
        Link: _BubbleActionLink
    }),
    Styles: S
})
